import { EValidationKey } from '@aca-new/app/shared/components/form-validation-message/shared/models/enums/validation-key.enum';
import { AfterContentInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, Input } from '@angular/core';
import { ValidationErrors } from '@angular/forms';
import { ControlsOf, FormControl, FormGroup } from '@ngneat/reactive-forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { QIMA_QUICK_ACCORDION_ANIMATION, QimaOptionalType } from '@qima/ngx-qima';
import { merge } from 'rxjs';

interface ICommonValidation {
  message: string;
  value: string;
}

@UntilDestroy()
@Component({
  standalone: false,
  selector: 'app-form-validation-message',
  templateUrl: './form-validation-message.component.html',
  styleUrls: ['./form-validation-message.component.scss'],
  animations: [QIMA_QUICK_ACCORDION_ANIMATION],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormValidationMessageComponent<T1, T2 extends Record<keyof T2, unknown>> implements AfterContentInit {
  /**
   * @description
   * The validation messages
   * @type {QimaOptionalType<Record<string, string>>}
   * @default undefined
   */
  @Input('formValidationMessages')
  public validationMessages: QimaOptionalType<Record<string, string>> = undefined;

  /**
   * @description
   * The control of form item
   * @type {QimaOptionalType<FormControl>}
   * @default undefined
   */
  @Input('formValidationMessageControl')
  public control: QimaOptionalType<FormControl<T1>> = undefined;

  /**
   * @description
   * The form group which contains this control
   * @type {QimaOptionalType<FormGroup<ControlsOf>>}
   * @default undefined
   */
  @Input('formValidationFormGroup')
  public formGroup: QimaOptionalType<FormGroup<ControlsOf<T2>>> = undefined;

  public validationMessage: string | undefined = undefined;
  public renderedHtml: string | undefined = undefined;
  public translateValue: string = '';
  public constructor(private readonly _changeDetectorRef: ChangeDetectorRef) {}

  public ngAfterContentInit(): void {
    this._initValidation();
  }

  public onAnimationEnd(): void {
    this.renderedHtml = this.validationMessage;
    this._changeDetectorRef.markForCheck();
  }

  private _initValidation(): void {
    const { control, formGroup } = this;

    this._initValidationMessages();

    if (control) {
      this._validateByFormControl();

      return;
    }

    if (formGroup) {
      this._validateByFormGroup();
    }
  }

  private _initValidationMessages(): void {
    const defaultValidationMessages = {
      addressMinLength: 'VALIDATIONS.ADDRESS_MIN_LENGTH',
      addressCharactersType: 'VALIDATIONS.ADDRESS_CHARACTERS_TYPE',
      allowInputEnglishCharacterOnly: 'VALIDATIONS.ENGLISH_ONLY',
      allowInputChineseOrEnglishCharacter: 'VALIDATIONS.ENGLISH_OR_CHINESE_ONLY',
      required: 'COMMON.REQUIRED',
      cityNotFound: 'COMMON.CITY_NOT_FOUND_SELECT_OR_REFINE',
      naturalNumbers: 'COMMON.NATURAL_NUMBER',
      maxlength: 'COMMON.MAXLENGTH',
      unicodeCharacter: 'COMMON.UNICODE_CHARACTER',
      max: 'COMMON.MAX',
      min: 'COMMON.MIN',
      email: 'COMMON.INVALID_EMAIL',
      positiveIntegerOrZero: 'COMMON.POSITIVE_INTEGER',
      invalidNumber: 'COMMON.INVALID_NUMBER',
    };

    if (!this.validationMessages) {
      this.validationMessages = {};
    }

    this.validationMessages = Object.assign(defaultValidationMessages, this.validationMessages);
  }

  // for single validation
  private _validateByFormControl(): void {
    const { control } = this;

    if (!control) {
      return;
    }

    // Check initial state immediately
    this._setMessage(control, this.validationMessages as Record<string, string>);

    const watch$ = merge(control.touch$, control.valueChanges, control.statusChanges);

    watch$.pipe(untilDestroyed(this)).subscribe((): void => {
      this._setMessage(control, this.validationMessages as Record<string, string>);
    });
  }

  // for crossing validation
  private _validateByFormGroup(): void {
    const { formGroup } = this;

    if (!formGroup) {
      return;
    }

    // Check initial state immediately
    this._setMessage(formGroup, this.validationMessages as Record<string, string>);

    const watch$ = merge(formGroup.touch$, formGroup.valueChanges, formGroup.statusChanges);

    watch$.pipe(untilDestroyed(this)).subscribe((): void => {
      this._setMessage(formGroup, this.validationMessages as Record<string, string>);
    });
  }

  private _setMessage(control: FormControl<T1> | FormGroup<ControlsOf<T2>>, validationMessages: Record<string, string>): void {
    this.validationMessage = undefined;

    if ((control.touched || control.dirty) && control.errors) {
      const errorKey: string = this._resolveErrorKey(control.errors);

      this.validationMessage = validationMessages[errorKey];

      if (errorKey === EValidationKey.COMMON) {
        this.validationMessage = (control.errors[EValidationKey.COMMON] as ICommonValidation).message;
        this.translateValue = (control.errors[EValidationKey.COMMON] as ICommonValidation).value;
      }

      this.renderedHtml = this.validationMessage;
    }

    this._changeDetectorRef.markForCheck();
  }

  /**
   * Picks which error key to show when a control has multiple validation errors.
   * Prefer cityNotFound over required after incomplete cascade (country/province without city).
   * Otherwise fall back to the first key in `errors` (UI shows one message at a time).
   * @param {ValidationErrors} errors - Control validation errors
   * @returns {string} Error key used for message lookup
   */
  private _resolveErrorKey(errors: ValidationErrors): string {
    if (errors.cityNotFound) {
      return 'cityNotFound';
    }

    // Default: first error key; no guaranteed priority among remaining validators
    return Object.keys(errors)[0];
  }
}
